import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatChipInputEvent } from '@angular/material/chips';
import { startWith, takeUntil } from 'rxjs/operators';
import { InputBaseComponent } from "@tva/ng-migration/uicomponents/base/input-base.component";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";

@Component({
  selector: "tva-chips",
  templateUrl: "./tv-action-chips.component.html",
  styleUrls: ["./tv-action-chips.component.scss"],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: TvActionChipsComponent,
    multi: true
  }]
})
export class TvActionChipsComponent extends InputBaseComponent {
  selectable = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER, COMMA];
  searchCtrl = new FormControl();
  filteredItems: any[];

  @Input() items: any[];
  @Input() placeHolder: string;
  @Input() title: string;
  @Input() itemValue: string;
  @Input() itemLabel: string;
  @Input() multiple = true;
  @Input() subtitle: string;

  @Output() selectionChanged: EventEmitter<any> = new EventEmitter<any>();

  get selectedItems(): any[] {
    return this.formControl.value
  }

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger, { read: MatAutocompleteTrigger }) autocomplete: MatAutocompleteTrigger;

  constructor() {
    super();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['items']?.firstChange && changes['items'].currentValue) {
      this.searchCtrl.valueChanges.pipe(
        startWith(null), takeUntil(this.destroyed$)).subscribe((item: string) => {
          if (item) {
            this.filteredItems = this.filter(item);
          } else {
            this.filteredItems = this.getAvailableItems().slice();
          }
        });
    }
    if (changes['formControl']?.isFirstChange() && !changes['formControl'].currentValue.value) {
      this.formControl.setValue([]);
    }
  }

  add(event: MatChipInputEvent): void {
    if (this.filteredItems.length && event.value) {
      if (this.filteredItems.length === 1) {
        const option = this.filteredItems[0];
        if (this.multiple) {
          this.selectedItems.push(option);
        } else {
          this.formControl.setValue([option]);
        }
      }
      this.registerChange();
      this.resetInput();
    }
  }

  remove(item: any): void {
    const index = this.selectedItems.indexOf(item);

    if (index >= 0) {
      this.selectedItems.splice(index, 1);
      this.formControl.setValue(this.selectedItems);
      this.filteredItems = this.getAvailableItems().slice();
      this.registerChange();
    }
  }

  changes() {
    this.selectionChanged.emit();
  }

  selected(value: any): void {
    if (!this.multiple) {
      this.formControl.setValue([value]);
    } else {
      this.selectedItems.push(value);
    }
    this.registerChange();
    this.resetInput();
  }

  getItemValue(item: any): any {
    if (this.itemValue) {
      return item[this.itemValue];
    }
    return item;
  }

  getItemLabel(item: any): any {
    if (this.itemLabel) {
      return item[this.itemLabel];
    }
    return item;
  }

  validate(): void {
    const errors = this.formControl.errors || {};
    if (!errors.required) {
      if (this.searchCtrl.value) {
        this.formControl.setErrors({ cleanRequired: true, ...errors });
      } else {
        delete errors.cleanRequired;
        const newErrors = Object.keys(errors).length ? { ...errors } : null;
        this.formControl.setErrors(newErrors);
      }
    }
  }

  private filter(value: string | object): string[] {
    if (typeof value === "string") {
      const filterValue = value.toLowerCase();
      return this.getAvailableItems().filter(item => this.getItemLabel(item).toLowerCase().includes(filterValue));
    } else {
      return this.getAvailableItems().filter(item => item != value);
    }
  }

  private getAvailableItems(): any[] {
    if (this.selectedItems) {
      return this.items.filter(item => !this.selectedItems.some(selectedItem => this.getItemValue(selectedItem) === this.getItemValue(item)));
    } else {
      return this.items;
    }
  }

  private resetInput(): void {
    this.searchInput.nativeElement.value = '';
    this.searchCtrl.setValue(null);
  }

  private registerChange(): void {
    this.formControl.markAsDirty();
    this.formControl.parent?.markAsDirty();
  }
}
